import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetCustomers,
  CreateCustomer,
  UpdateCustomer,
  DeleteCustomer,
  ToggleCustomerStatus,
} from "../Api";
import { ServiceQueryOptions } from "./query_helpers";
import { LOOKUP_STALE_TIME } from "../Constant/AppConstants";

export const customerQueryKeys = {
  customers: () => ["customers"] as const,
};

export const invalidateCustomerQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["customers"] });
};

export const useCustomersQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: customerQueryKeys.customers(),
    queryFn: async () => await GetCustomers(),
  });
};

export const useCustomersRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      queryClient.fetchQuery({
        staleTime: LOOKUP_STALE_TIME,
        queryKey: customerQueryKeys.customers(),
        queryFn: async () => await GetCustomers(),
      }),
  });
};

export const useCreateCustomerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateCustomer>[0]) =>
      CreateCustomer(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateCustomerQueries(queryClient);
      }
    },
  });
};

export const useUpdateCustomerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdateCustomer>[0]) =>
      UpdateCustomer(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateCustomerQueries(queryClient);
      }
    },
  });
};

export const useDeleteCustomerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => DeleteCustomer(id),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateCustomerQueries(queryClient);
      }
    },
  });
};

export const useToggleCustomerStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { id: number; status: string }) =>
      ToggleCustomerStatus(String(payload.id), payload.status),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateCustomerQueries(queryClient);
      }
    },
  });
};

export const useCustomerService = (config?: {
  fetchCustomersEnabled?: boolean;
}) => {
  const customersQuery = useCustomersQuery({
    enabled: config?.fetchCustomersEnabled !== false,
  });

  const createCustomerMutation = useCreateCustomerMutation();
  const updateCustomerMutation = useUpdateCustomerMutation();
  const deleteCustomerMutation = useDeleteCustomerMutation();
  const toggleCustomerStatusMutation = useToggleCustomerStatusMutation();

  return {
    customersQuery,
    createCustomerMutation,
    updateCustomerMutation,
    deleteCustomerMutation,
    toggleCustomerStatusMutation,
  };
};
